import { r as registerInstance, c as createEvent, h, H as Host } from './index-662ec80e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { b as vMinLength, a as vString, S as vOneOf, W as vArrayOf, J as vNumber, Q as vBoolean } from './validation-02b8d01d.js';
import { c as currentLocale, t } from './i18n-939e2dee.js';
import { a as continentCodes, c as countries, l as langs, r as returnTypes } from './countries-358acdc0.js';
import { w as waitNextRender, r as resolveNextRender } from './browser-202fc7f4.js';
import './types-6f6b2fba.js';

const dsCountryDialogCss = "ds-country-dialog{display:block;max-width:16rem;width:100%}ds-country-dialog .hidden-input{position:absolute;opacity:0;margin-top:0.5rem;width:3rem;pointer-events:none}ds-country-dialog>label{display:block;font-size:0.88888889rem;line-height:1.56rem;margin-bottom:0.25rem !important;color:var(--coolGrey-700);font-weight:500}ds-country-dialog[is-required]:not([label=\"\"])>label::after{content:\"*\";margin-left:0.25rem}ds-country-dialog .help-error{margin:0.45rem 1.25rem 0.5rem;font-size:0.79012346rem;color:var(--red-500) !important;line-height:1.3rem;max-width:max-content}ds-country-dialog .dialog-container{margin:-1rem 1rem 0 0;width:55rem}ds-country-dialog .dialog-container .dialog-header{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:justify;justify-content:space-between}ds-country-dialog .dialog-container .dialog-header h2{margin:1rem 0}ds-country-dialog .dialog-container .dialog-header ds-tag{margin-left:1rem}ds-country-dialog .dialog-container .sub-heading{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:justify;justify-content:space-between;border-bottom:1px SOLID var(--grey-400);padding-bottom:0.5rem;margin-bottom:1rem}ds-country-dialog .dialog-container .sub-heading ds-input-checkbox label{margin-left:0.5rem;font-weight:bold}ds-country-dialog .dialog-container .sub-heading .search{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center}ds-country-dialog .dialog-container .sub-heading .search ds-icon{margin:0 0.5rem 0.25rem 0}ds-country-dialog .dialog-container .sub-heading .search ds-input-string{width:21rem}ds-country-dialog .dialog-container .scrollable-container{height:calc(100vh - 20rem);overflow-y:scroll;margin-right:-2rem;padding-right:2rem;scrollbar-width:thin}ds-country-dialog .dialog-container .continent{background:var(--grey-400);font-weight:bold;margin-bottom:0.5rem;margin-right:-0.5rem;padding:0.5rem}ds-country-dialog .dialog-container .countries{display:-ms-flexbox;display:flex;-ms-flex-wrap:wrap;flex-wrap:wrap;margin-left:0.5rem;margin-bottom:0.5rem}ds-country-dialog .dialog-container .countries ds-input-checkbox{width:18rem;margin-bottom:0.25rem}ds-country-dialog .dialog-container .buttons{padding-top:1rem !important;display:-ms-flexbox;display:flex;-ms-flex-align:stretch;align-items:stretch;-ms-flex-pack:end;justify-content:flex-end}ds-country-dialog .dialog-container .buttons>ds-button+ds-button{margin-left:1rem !important}";

const DsCountryDialog = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.dsInput = createEvent(this, "dsInput", 7);
    this.nextRenderPromises = [];
    this.countriesByContinent = continentCodes.reduce((acc, code) => (Object.assign(Object.assign({}, acc), { [code]: countries.filter(country => country.continent === code) })), {});
    this.propError = {};
    this.currentLang = 'en';
    this.currentLocale = currentLocale();
    this.name = undefined;
    this.label = '';
    this.returnType = 'numeric';
    this.internalValue = [];
    this.value = [];
    this.isDisabled = false;
    this.isReadonly = false;
    this.isRequired = false;
    this.displayError = false;
    this.validationError = undefined;
    this.injectedError = undefined;
    this.isOpen = false;
    this.filter = '';
  }
  updateLocale() {
    this.currentLocale = currentLocale();
    const lng = this.currentLocale.substring(0, 2).toLowerCase();
    if (langs.includes(lng))
      this.currentLang = lng;
    else
      this.currentLang = 'en';
  }
  watchName() {
    /* istanbul ignore if */
    if (!propCheck(this, 'name', 'string', vMinLength(1)))
      return;
  }
  watchLabel() {
    /* istanbul ignore if */
    if (!propCheck(this, 'label', 'string', vString))
      return;
  }
  watchReturnType(_, prevReturnType) {
    /* istanbul ignore if */
    if (!propCheck(this, 'returnType', returnTypes.join(' | '), vOneOf(returnTypes)))
      return;
    if (this.value.length > 0 && prevReturnType) {
      const currentCountries = this.value.map(value => countries.find(c => c[prevReturnType] === value));
      this.dsInput.emit(currentCountries.map(country => country[this.returnType]));
    }
  }
  watchValue() {
    /* istanbul ignore if */
    if (!propCheck(this, 'value', 'Array<string | number>', vArrayOf(v => vString(v) && vNumber(v))))
      return;
    this.injectedError = undefined;
    this.validate(this.value);
    this.internalValue = this.value;
  }
  async watchIsDisabled() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isDisabled', 'boolean', vBoolean))
      return;
    if (this.isDisabled) {
      this.dsInput.emit([]);
      await waitNextRender(this);
      this.validate([]);
    }
    else {
      await waitNextRender(this);
      this.validate(this.value);
    }
  }
  async watchIsReadonly() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isReadonly', 'boolean', vBoolean))
      return;
    await waitNextRender(this);
    this.validate(this.value);
  }
  async watchIsRequired() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isRequired', 'boolean', vBoolean))
      return;
    this.validate(this.value);
  }
  async injectError(error) {
    this.injectedError = error;
    this.validate(this.value);
    this.displayError = true;
  }
  async hideError() {
    this.injectedError = undefined;
    this.validate(this.value);
    this.displayError = false;
  }
  validate(value) {
    if (!this.inputElement)
      return true;
    this.inputElement.setCustomValidity('');
    this.validationError = undefined;
    if (this.isDisabled || this.isReadonly) {
      if (this.dsForm)
        this.dsForm.checkValidity();
      return true;
    }
    if (this.isRequired && value.length === 0) {
      this.validationError = t('validation.required');
    }
    if (this.validationError) {
      this.inputElement.setCustomValidity(this.validationError);
    }
    else if (this.injectedError) {
      this.inputElement.setCustomValidity(this.injectedError);
    }
    if (this.dsForm)
      this.dsForm.checkValidity();
    return !!this.validationError;
  }
  connectedCallback() {
    runWatchers(this);
    this.updateLocale();
  }
  componentDidLoad() {
    if (this.inputElement)
      this.dsForm = this.inputElement.closest('ds-form');
    this.validate(this.value);
  }
  componentDidRender() {
    resolveNextRender(this);
  }
  disconnectedCallback() {
    if (this.dsForm)
      this.dsForm.checkValidity();
  }
  render() {
    if (hasPropError(this))
      return displayPropError(this);
    const countryMatchesFilter = (country) => country.name[this.currentLang].toLocaleLowerCase().indexOf(this.filter.toLocaleLowerCase()) !== -1;
    const error = this.displayError && (this.injectedError || this.validationError);
    return (h(Host, null, h("label", { htmlFor: this.name }, this.label), h("input", { ref: el => this.inputElement = el, type: "text", class: "hidden-input", name: this.name, value: this.value.join(','), disabled: this.isDisabled, readonly: this.isReadonly, onInvalid: () => { this.displayError = true; } }), h("ds-button", { label: `${this.value.length} ${this.value.length === 1 ? t('country-multiple.country') : t('country-multiple.country_plural')}`, mode: "outlined", version: "monochrome", type: "button", isDisabled: this.isDisabled || this.isReadonly, onClick: () => {
        this.dialog.open();
        this.isOpen = true;
      }, icon: "location", isReverse: true, isFullWidth: true }), error && h("div", { class: "help-error" }, error), h("ds-dialog", { ref: el => this.dialog = el, isDismissible: false, isModal: true, onClosed: () => {
        this.isOpen = false;
        this.displayError = true;
      } }, h("div", { class: "dialog-container" }, this.isOpen ?
      h("div", null, h("div", { class: "dialog-header" }, h("h2", null, t('country-multiple.allowedCountries')), h("div", null, h("ds-tag", { version: "success", icon: "check-circle", iconSize: "1rem", label: `${t('country-multiple.allowedCountries')}: ${this.internalValue.length}`, isSquare: true }), h("ds-tag", { version: "error", icon: "block", iconSize: "1rem", label: `${t('country-multiple.deniedCountries')}: ${countries.length - this.internalValue.length}`, isSquare: true }))), h("div", { class: "sub-heading" }, h("ds-input-checkbox", { name: "all", label: t('country-multiple.all'), checkedValue: true, value: this.internalValue.length === countries.length, onDsInput: (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.detail) {
            this.internalValue = countries.map(country => country[this.returnType]);
          }
          else {
            this.internalValue = [];
          }
        } }), h("div", { class: "search" }, h("ds-icon", { name: "search", color: "var(--coolGrey-400)" }), h("ds-input-string", { name: this.name + '_search', placeholder: t('country-multiple.searchCountries'), onDsInput: (e) => {
          var _a;
          e.preventDefault();
          e.stopPropagation();
          this.filter = (_a = e.detail) !== null && _a !== void 0 ? _a : '';
        }, value: this.filter, isMini: true, isClearable: true }))), h("div", { class: "scrollable-container" }, Object.keys(this.countriesByContinent).map(continentCode => {
        const countries = this.countriesByContinent[continentCode];
        const isDisplaying = countries.some(countryMatchesFilter);
        return h("div", { style: isDisplaying ? {} : { display: 'none' } }, h("div", { class: "continent" }, h("ds-input-checkbox", { name: this.name + '_' + continentCode, onDsInput: (e) => {
            e.preventDefault();
            e.stopPropagation();
            const continentsCountryValues = countries.map(c => c[this.returnType]);
            // filter existing values out
            let newValue = this.internalValue.filter(country => !continentsCountryValues.includes(country));
            if (e.detail) {
              newValue = newValue.concat(continentsCountryValues);
              this.internalValue = newValue;
            }
            else {
              this.internalValue = newValue;
            }
          }, label: t(`country-multiple.continents.${continentCode}`), checkedValue: true, value: countries.every(country => this.internalValue.includes(country[this.returnType])) })), h("div", { class: "countries" }, this.countriesByContinent[continentCode].map(country => {
          const hidden = !countryMatchesFilter(country);
          return h("ds-input-checkbox", { name: this.name + '[]', label: country.name[this.currentLang], checkedValue: country[this.returnType], value: this.internalValue, style: hidden ? { display: 'none' } : {}, onDsInput: e => {
              e.preventDefault();
              e.stopPropagation();
              this.internalValue = e.detail;
            } });
        })));
      })), h("div", { class: "buttons" }, h("ds-button", { label: t('country-multiple.cancel'), version: "secondary", onClick: () => {
          this.internalValue = this.value;
          this.dialog.close();
        } }), h("ds-button", { label: t('country-multiple.save'), version: "primary", onClick: () => {
          this.dsInput.emit(this.internalValue);
          this.dialog.close();
        } })))
      : ''))));
  }
  static get watchers() { return {
    "name": ["watchName"],
    "label": ["watchLabel"],
    "returnType": ["watchReturnType"],
    "value": ["watchValue"],
    "isDisabled": ["watchIsDisabled"],
    "isReadonly": ["watchIsReadonly"],
    "isRequired": ["watchIsRequired"]
  }; }
};
DsCountryDialog.style = dsCountryDialogCss;

export { DsCountryDialog as ds_country_dialog };
